import React from 'react';
import { Trash2, Plus, Calendar, ChevronDown } from 'lucide-react';

/**
 * Helper to get ambulance type short name or abbreviation from object or string
 */
export const getAmbulanceTypeShortName = (typeOrName) => {
  if (!typeOrName) return '';
  if (typeof typeOrName === 'object') {
    return typeOrName.abbreviation || typeOrName.name || '';
  }
  return String(typeOrName);
};

/**
 * Helper to get clean property key for dynamic type state
 */
export const getAmbulanceTypeKey = (type) => {
  if (!type) return 'type';
  if (typeof type === 'object' && type.type_id) {
    return type.type_id;
  }
  const name = typeof type === 'string' ? type : type.name || '';
  return name.toLowerCase().replace(/[^a-z0-9]/g, '_');
};

export const DynamicMonthlyDemandTable = ({ rows = [], onChange, ambulanceTypes = [] }) => {
  // Dynamically map types purely from backend database lookups (no hardcoded dummy fallback)
  const dynamicTypes = React.useMemo(() => {
    if (Array.isArray(ambulanceTypes) && ambulanceTypes.length > 0) {
      return ambulanceTypes.map((t) => ({
        id: t.type_id || t.id || t.name,
        name: t.name,
        shortName: t.abbreviation || t.name,
        key: getAmbulanceTypeKey(t)
      }));
    }
    return [];
  }, [ambulanceTypes]);

  const handleAddRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `month-${window.crypto.randomUUID()}` : `month-${Math.random().toString(36).slice(2, 9)}`,
      month: '',
      ambulance_type: '',
      total: '',
      inbound: '',
      outbound: '',
      unserved: ''
    };
    onChange([...rows, newRow]);
  };

  const handleRemoveRow = (indexToRemove) => {
    if (rows.length <= 1) return;
    const filtered = rows.filter((_, idx) => idx !== indexToRemove);
    onChange(filtered);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = rows.map((row, idx) => {
      if (idx === index) {
        return { ...row, [field]: value };
      }
      return row;
    });
    onChange(updated);
  };

  return (
    <div className="w-full">
      {/* Title & Add Row Action */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 leading-tight">
              Monthly transport volume
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Historical patient transfer calls across months (optional estimates).
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          aria-label="Add new monthly demand row"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium cursor-pointer transition-colors shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add month</span>
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50/80 text-slate-600 font-semibold border-b border-slate-200">
              <th className="py-3 px-3 w-10 text-center">#</th>
              <th className="py-3 px-3 min-w-[130px]">Month</th>
              <th className="py-3 px-3 min-w-[170px]">Ambulance Type</th>
              <th className="py-3 px-3 min-w-[95px] text-center">Total Calls</th>
              <th className="py-3 px-3 min-w-[95px] text-center">Inbound</th>
              <th className="py-3 px-3 min-w-[95px] text-center">Outbound</th>
              <th className="py-3 px-3 min-w-[95px] text-center">Unserved</th>
              <th className="py-3 px-2 w-10 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((row, idx) => {
              const selectedAmbulanceType = row.ambulance_type || '';

              return (
                <tr key={row.id || idx} className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-2.5 px-3 text-center text-slate-400 font-medium">
                    {idx + 1}
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="month"
                      aria-label={`Month for row ${idx + 1}`}
                      value={row.month || ''}
                      onChange={(e) => handleFieldChange(idx, 'month', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </td>
                  <td className="py-2.5 px-3 min-w-[170px]">
                    <div className="relative">
                      <select
                        aria-label={`Ambulance Type for row ${idx + 1}`}
                        value={selectedAmbulanceType}
                        onChange={(e) => handleFieldChange(idx, 'ambulance_type', e.target.value)}
                        className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                        title={
                          dynamicTypes.find(
                            (t) => t.shortName === selectedAmbulanceType || t.name === selectedAmbulanceType
                          )?.name || 'Select ambulance type'
                        }
                      >
                        <option value="">
                          {dynamicTypes.length === 0 ? 'Loading types from server...' : 'Select Ambulance Type'}
                        </option>
                        {dynamicTypes.map((t) => (
                          <option key={t.id || t.key} value={t.shortName} title={t.name}>
                            {t.shortName}
                            {/* Full name commented out, hover shows full name via title tooltip */}
                            {/* {` (${t.name})`} */}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="Total"
                      aria-label={`Total calls for row ${idx + 1}`}
                      value={row.total || ''}
                      onChange={(e) => handleFieldChange(idx, 'total', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-center"
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="Inbound"
                      aria-label={`Inbound calls for row ${idx + 1}`}
                      value={row.inbound || ''}
                      onChange={(e) => handleFieldChange(idx, 'inbound', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-center"
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="Outbound"
                      aria-label={`Outbound calls for row ${idx + 1}`}
                      value={row.outbound || ''}
                      onChange={(e) => handleFieldChange(idx, 'outbound', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-center"
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="Unserved"
                      aria-label={`Unserved calls for row ${idx + 1}`}
                      value={row.unserved || ''}
                      onChange={(e) => handleFieldChange(idx, 'unserved', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-center"
                    />
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        aria-label={`Remove month row ${idx + 1}`}
                        className="text-slate-400 hover:text-red-600 transition-colors p-1 rounded-md cursor-pointer"
                        title="Remove row"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicMonthlyDemandTable;
